import { ChangeEvent, useCallback, useEffect, useRef } from 'react';
import Checkbox from '__components/Checkbox/Checkbox';
import Icon from '__components/Kizen/Icon';
import { useTooltip } from '__components/Kizen/Tooltip';
import { LabelInfoIconSizing } from '../styles';
import Validation from '__components/Inputs/Validation';
import { useSetupAssistant } from '@kizenapps/engine/react';
import { AssistantField } from '@kizenapps/engine';
import { Typography } from '@kizen/kds/Typography';
import { Spacer } from '@kizen/kds/Spacer';
import styled from '@emotion/styled';

const ExpandedBooleanWrapper = styled.div<{ indentLevel?: number }>`
  border: solid 1px var(--color-border-medium);
  border-radius: 4px;
  padding: 15px;
  margin-left: ${({ indentLevel }) =>
    indentLevel ? `${indentLevel * 20}px` : '0'};
`;

export const BooleanBlock = ({
  field,
  disabled = false,
}: {
  field: AssistantField & {
    expanded?: boolean; // Expanded means we expand the default rendering to some custom UI
    expandedLabel?: string; // For synthetic menu booleans, we want to show the linked object in the label
    indentLevel?: number; // For synthetic menu booleans, we want to indent them to show hierarchy
  };
  disabled?: boolean;
}) => {
  const {
    state,
    setState,
    evaluateExpression,
    shouldHideField,
    getFieldErrorState,
    registerFieldResetter,
    afterFieldChange,
    disabledKeys,
  } = useSetupAssistant();

  const value = (state as any)[field.key]?.value ?? field.default ?? false;

  const [tooltipProps, tooltip] = useTooltip({ label: field.tooltip });

  const isDisabled = disabledKeys.includes(field.key) || disabled;

  const setBooleanValue = useCallback(
    (newValue: boolean | undefined) => {
      setState((prevState: any) => ({
        ...prevState,
        [field.key]: {
          type: 'boolean',
          value: newValue,
        },
      }));

      afterFieldChange(field.key);
    },
    [field.key, setState, afterFieldChange]
  );

  useEffect(() => {
    registerFieldResetter(field.key, () => {
      setBooleanValue(undefined);
    });
  }, [field.key, registerFieldResetter, setBooleanValue]);

  useEffect(() => {
    if (field.when) {
      evaluateExpression(field.when, field.key);
    }
  }, [field.when, evaluateExpression, field.key]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  if (shouldHideField(field.key)) {
    return null;
  }

  const errorState = getFieldErrorState(field.key);

  return (
    <>
      <div ref={wrapperRef}>
        {field.expanded ? (
          <ExpandedBooleanWrapper indentLevel={field.indentLevel}>
            <Typography>{field.expandedLabel || field.label}</Typography>
            <Spacer size={15} />
            <Checkbox
              checked={value}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setBooleanValue(e.target.checked)
              }
              label={field.expandedLabel ? field.label : ''}
              inline
              disabled={isDisabled}
              grow
              error={errorState?.error}
            />
            <Validation target={wrapperRef.current} {...errorState} inModal />
          </ExpandedBooleanWrapper>
        ) : (
          <>
            <Checkbox
              checked={value}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setBooleanValue(e.target.checked)
              }
              label={
                <>
                  {field.label}
                  {field.required && '*'}
                  {field.tooltip && (
                    <LabelInfoIconSizing size="10px">
                      <Icon icon="info-circle" {...tooltipProps} />
                    </LabelInfoIconSizing>
                  )}
                  {tooltip}
                </>
              }
              inline
              disabled={isDisabled}
              grow
              error={errorState?.error}
            />
            <Validation target={wrapperRef.current} {...errorState} inModal />
          </>
        )}
      </div>
    </>
  );
};
